/**
 * Container Template Selector Modal
 * Provides an intuitive UI for selecting and creating containers
 */

import { useState, useCallback, useEffect } from 'react'
import {
  ContainerTemplate,
  getTemplatesByCategory,
  ContainerType
} from '../../templates/containerTemplates'
import { getContainerStyle } from '../../config/containerStyles'
import type { CloudProvider } from '../../types/schema'

interface ContainerTemplateSelectorProps {
  isOpen: boolean
  onClose: () => void
  onCreate: (template: ContainerTemplate) => void
}

type TabType = 'templates' | 'custom'

export default function ContainerTemplateSelector({
  isOpen,
  onClose,
  onCreate
}: ContainerTemplateSelectorProps) {
  const [activeTab, setActiveTab] = useState<TabType>('templates')
  const [customLabel, setCustomLabel] = useState('')
  const [customType, setCustomType] = useState<ContainerType>('layer')
  const [customProvider, setCustomProvider] = useState<CloudProvider>('generic')
  const [labelError, setLabelError] = useState('')

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('templates')
      setCustomLabel('')
      setCustomType('layer')
      setCustomProvider('generic')
      setLabelError('')
    }
  }, [isOpen])

  const handleTemplateClick = useCallback((template: ContainerTemplate) => {
    onCreate(template)
    onClose()
  }, [onCreate, onClose])

  const handleCustomCreate = useCallback(() => {
    if (!customLabel.trim()) {
      setLabelError('Please enter a container label')
      return
    }

    const style = getContainerStyle(customType, customType === 'cloud-provider' ? customProvider : undefined)

    const customTemplate: ContainerTemplate = {
      id: `custom-${Date.now()}`,
      name: customLabel,
      description: 'Custom container',
      type: customType,
      provider: customType === 'cloud-provider' ? customProvider : undefined,
      icon: style.icon,
      size: { width: 600, height: 400 },
      category: customType === 'cloud-provider' ? 'cloud' :
                customType === 'network-zone' ? 'network' :
                customType === 'service-group' ? 'services' : 'architecture'
    }

    onCreate(customTemplate)
    onClose()
  }, [customLabel, customType, customProvider, onCreate, onClose])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    } else if (e.key === 'Enter' && activeTab === 'custom') {
      handleCustomCreate()
    }
  }, [onClose, activeTab, handleCustomCreate])

  if (!isOpen) return null

  // Get templates by category
  const cloudTemplates = getTemplatesByCategory('cloud')
  const architectureTemplates = getTemplatesByCategory('architecture')
  const networkTemplates = getTemplatesByCategory('network')
  const serviceTemplates = getTemplatesByCategory('services')

  return (
    <div
      className="container-modal-overlay"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        className="container-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '700px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
            Create Container
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0 4px',
              lineHeight: 1
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e5e7eb',
            padding: '0 24px'
          }}
        >
          <button
            onClick={() => setActiveTab('templates')}
            style={{
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'templates' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'templates' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'templates' ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            style={{
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'custom' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'custom' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'custom' ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Custom
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px'
          }}
        >
          {activeTab === 'templates' ? (
            <div>
              {/* Cloud Providers */}
              {cloudTemplates.length > 0 && (
                <TemplateSection
                  title="Cloud Providers"
                  templates={cloudTemplates}
                  onSelect={handleTemplateClick}
                />
              )}

              {/* Architectural Layers */}
              {architectureTemplates.length > 0 && (
                <TemplateSection
                  title="Architectural Layers"
                  templates={architectureTemplates}
                  onSelect={handleTemplateClick}
                />
              )}

              {/* Network Zones */}
              {networkTemplates.length > 0 && (
                <TemplateSection
                  title="Network Zones"
                  templates={networkTemplates}
                  onSelect={handleTemplateClick}
                />
              )}

              {/* Service Groups */}
              {serviceTemplates.length > 0 && (
                <TemplateSection
                  title="Service Groups"
                  templates={serviceTemplates}
                  onSelect={handleTemplateClick}
                />
              )}
            </div>
          ) : (
            // Custom Tab
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div style={{ marginBottom: '20px' }}>
                <label
                  htmlFor="container-label"
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                  }}
                >
                  Container Label
                </label>
                <input
                  id="container-label"
                  type="text"
                  value={customLabel}
                  onChange={(e) => {
                    setCustomLabel(e.target.value)
                    if (labelError) setLabelError('')
                  }}
                  placeholder="e.g., Production Environment"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: labelError ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = labelError ? '#ef4444' : '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = labelError ? '#ef4444' : '#d1d5db'}
                />
                {labelError && (
                  <div style={{ marginTop: '6px', fontSize: '13px', color: '#ef4444' }}>
                    {labelError}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label
                  htmlFor="container-type"
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                  }}
                >
                  Container Type
                </label>
                <select
                  id="container-type"
                  value={customType}
                  onChange={(e) => setCustomType(e.target.value as ContainerType)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="cloud-provider">Cloud Provider</option>
                  <option value="layer">Architectural Layer</option>
                  <option value="network-zone">Network Zone</option>
                  <option value="service-group">Service Group</option>
                </select>
              </div>

              {customType === 'cloud-provider' && (
                <div style={{ marginBottom: '20px' }}>
                  <label
                    htmlFor="cloud-provider"
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151'
                    }}
                  >
                    Cloud Provider
                  </label>
                  <select
                    id="cloud-provider"
                    value={customProvider}
                    onChange={(e) => setCustomProvider(e.target.value as CloudProvider)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="aws">AWS</option>
                    <option value="azure">Azure</option>
                    <option value="gcp">GCP</option>
                    <option value="generic">Generic Cloud</option>
                  </select>
                </div>
              )}

              <button
                onClick={handleCustomCreate}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  marginTop: '12px'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                Create Container
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// TEMPLATE SECTION COMPONENT
// ============================================================================

interface TemplateSectionProps {
  title: string
  templates: ContainerTemplate[]
  onSelect: (template: ContainerTemplate) => void
}

function TemplateSection({ title, templates, onSelect }: TemplateSectionProps) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <h3
        style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#6b7280',
          marginBottom: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}
      >
        {title}
      </h3>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: '12px'
        }}
      >
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// TEMPLATE CARD COMPONENT
// ============================================================================

interface TemplateCardProps {
  template: ContainerTemplate
  onSelect: (template: ContainerTemplate) => void
}

function TemplateCard({ template, onSelect }: TemplateCardProps) {
  const style = getContainerStyle(template.type, template.provider)

  return (
    <button
      onClick={() => onSelect(template)}
      style={{
        padding: '16px',
        border: `2px solid ${style.borderColor}`,
        borderRadius: '8px',
        backgroundColor: 'white',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px'
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = `${style.bgColor}15`
        e.currentTarget.style.transform = 'scale(1.05)'
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = 'white'
        e.currentTarget.style.transform = 'scale(1)'
      }}
      title={template.description}
    >
      <div style={{ fontSize: '32px' }}>
        {template.icon}
      </div>
      <div
        style={{
          fontSize: '13px',
          fontWeight: '500',
          color: '#374151',
          lineHeight: 1.3
        }}
      >
        {template.name}
      </div>
    </button>
  )
}
